import React, { useEffect, useState } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../../../Utils/loader";
import { UrduInput } from "../../../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
} from "../../../../../Components/Shared/ImageSeoFields";
import { UiModal } from "../../../../../Components/Shared/UiModal";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;

const EMPTY_FORM = {
  name: "",
  name_ur: "",
  description: "",
  description_ur: "",
  custom_url: "",
  image: null,
  imagePreview: null,
  image_alt: "",
  image_title: "",
};

const toMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("blob:")) return path;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

const checkImageDimension = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = function () {
      URL.revokeObjectURL(objectUrl);
      resolve(this.width === REQUIRED_WIDTH && this.height === REQUIRED_HEIGHT);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image"));
    };
    img.src = objectUrl;
  });

function ServicesCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
  isSubmitting,
}) {
  const [categoryData, setCategoryData] = useState(EMPTY_FORM);
  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [imageError, setImageError] = useState("");
  const [customUrlError, setCustomUrlError] = useState("");

  const revokeBlobPreview = (preview) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      setCategoryData((prev) => {
        revokeBlobPreview(prev.imagePreview);
        return EMPTY_FORM;
      });
      setNameError("");
      setDescriptionError("");
      setImageError("");
      setCustomUrlError("");
      return;
    }

    if (category) {
      setCategoryData({
        name: category.name || "",
        name_ur: category.name_ur || "",
        description: category.description || "",
        description_ur: category.description_ur || "",
        custom_url: (category.custom_url || "").replace(/\/$/, ""),
        image: null,
        imagePreview: toMediaUrl(category.image),
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
      });
    } else {
      setCategoryData(EMPTY_FORM);
    }
    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");
  }, [modalOpen, category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "custom_url") {
      setCustomUrlError("");
      if (value.startsWith("/")) {
        setCustomUrlError("Custom URL should not start with '/'");
      }
    }
    if (name === "name" && nameError) setNameError("");
    if (name === "description" && descriptionError) setDescriptionError("");
    setCategoryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImageError("");
    const isValid = await checkImageDimension(file);
    if (!isValid) {
      setImageError(
        `Image dimensions must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
      );
      return;
    }

    setCategoryData((prev) => {
      revokeBlobPreview(prev.imagePreview);
      return {
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      };
    });
  };

  const removeImage = () => {
    setCategoryData((prev) => {
      revokeBlobPreview(prev.imagePreview);
      return {
        ...prev,
        image: null,
        imagePreview: null,
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");

    let hasError = false;

    if (!categoryData.name.trim()) {
      setNameError("Category name is required.");
      hasError = true;
    }

    if (!categoryData.custom_url.trim()) {
      setCustomUrlError("Custom URL is required.");
      hasError = true;
    } else if (categoryData.custom_url.trim().startsWith("/")) {
      setCustomUrlError("Custom URL should not start with '/'");
      hasError = true;
    }

    if (!categoryData.description.trim()) {
      setDescriptionError("Description is required.");
      hasError = true;
    }

    if (!categoryData.image && !categoryData.imagePreview) {
      setImageError("Image is required.");
      hasError = true;
    }

    if (hasError) return;

    const formData = new FormData();
    formData.append("name", categoryData.name.trim());
    formData.append("custom_url", categoryData.custom_url.trim());
    if (categoryData.name_ur) {
      formData.append("name_ur", categoryData.name_ur.trim());
    }
    if (categoryData.description) {
      formData.append("description", categoryData.description.trim());
    }
    if (categoryData.description_ur) {
      formData.append("description_ur", categoryData.description_ur.trim());
    }
    if (categoryData.image) {
      formData.append("image", categoryData.image);
    }
    appendImageSeoToFormData(
      formData,
      categoryData.image_alt,
      categoryData.image_title,
    );

    handleSaveCategory(formData, category ? category.id : null);
  };

  const handleClose = () => {
    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");
    toggleModal();
  };

  const isEditMode = Boolean(category);

  return (
    <UiModal
      isOpen={modalOpen}
      onClose={handleClose}
      eyebrow="Services"
      title={isEditMode ? "Edit Category" : "Add Category"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="ui-btn-cancel min-w-[108px]"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ui-btn-save min-w-[108px]"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Category details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleInputChange}
              className={`ui-input-field ${
                nameError ? "border-brand-error" : ""
              }`}
              placeholder="Enter category name"
            />
            {nameError ? <p className="ui-form-error">{nameError}</p> : null}
            <UrduInput
              label="نام"
              name="name_ur"
              value={categoryData.name_ur}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Custom URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_url"
              value={categoryData.custom_url}
              onChange={handleInputChange}
              placeholder="e.g. construction-services"
              className={`ui-input-field ${
                customUrlError ? "border-brand-error" : ""
              }`}
            />
            {customUrlError ? (
              <p className="ui-form-error">{customUrlError}</p>
            ) : null}
            <p className="mt-1 text-xs text-gray-500">
              Do not start with "/". Backend will slugify and ensure uniqueness.
            </p>
          </div>

          <div>
            <label className="ui-form-label">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={categoryData.description}
              onChange={handleInputChange}
              maxLength={100}
              placeholder="Brief description (max 100 characters)"
              className={`ui-input-field ${
                descriptionError ? "border-brand-error" : ""
              }`}
            />
            <div className="mt-1 flex justify-between">
              {descriptionError ? (
                <p className="ui-form-error">{descriptionError}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-500">
                {categoryData.description.length}/100 characters
              </p>
            </div>
            <UrduInput
              label="تفصیل"
              name="description_ur"
              value={categoryData.description_ur}
              onChange={handleInputChange}
              type="textarea"
              rows={2}
            />
          </div>
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Image <span className="text-red-500">*</span>
          </label>

          <input
            id="service-category-image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />

          {categoryData.imagePreview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={categoryData.imagePreview}
                  alt="Category preview"
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={removeImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="service-category-image"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="service-category-image"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                Required size: {REQUIRED_WIDTH} × {REQUIRED_HEIGHT} pixels
              </span>
            </label>
          )}

          {imageError ? <p className="ui-form-error">{imageError}</p> : null}
          <ImageSeoFields
            imageAlt={categoryData.image_alt}
            imageTitle={categoryData.image_title}
            onAltChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_title: value }))
            }
          />
        </div>
      </section>
    </UiModal>
  );
}

export default ServicesCategoryModal;
